import { Box, BoxProps, Stack } from "@mui/material";
import { PDFDocumentProxy } from "pdfjs-dist";
import { useMemo } from "react";
import { useState } from "react";
import { useEffect, useRef } from 'react';

type PDFViewerProps = BoxProps & {
  url: string;
}

export default function PDFViewer({ sx, url }: PDFViewerProps){
	const canvasRef = useRef<(HTMLCanvasElement | null)[]>([]);
	const containerRef = useRef<HTMLDivElement>(null);
  const pdfPromise = useMemo(async () => {
    if(!url) return null;
    try {
      const pdfJS = await import('pdfjs-dist');
      pdfJS.GlobalWorkerOptions.workerSrc =
        window.location.origin + '/pdf.worker.min.mjs';
      return await pdfJS.getDocument(url).promise;
    } catch {
      return Promise.reject();
    }
  }, [url]);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [rendered, setRendered] = useState(false);
  const [pdfJsIncompatible, setPdfJsIncompatible] = useState(false);

  useEffect(() => {
    if (!pdf || !rendered) return;
    (async () => {
      for (let i = 0; i < pdf.numPages; i++) {
        const page = await pdf.getPage(i + 1);
        const canvas = canvasRef.current[i];
        if (!canvas) continue;
        let viewport = page.getViewport({ scale: 2 });
        if(containerRef.current){
          const proportion = viewport.height / viewport.width;
          const width = containerRef.current.clientWidth;
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          canvas.style.height = (proportion * width).toString();
          canvas.style.width = width.toString();
        }
        const canvasContext = canvas.getContext('2d');
        if(canvasContext){
          const renderContext = { canvas, canvasContext, viewport };
          page.render(renderContext);
        }
      }
    })()
  }, [pdf, rendered]);

	useEffect(() => {
    if (pdfPromise) {
      pdfPromise.then((pdf) => {
        setPdf(pdf);
      }).catch(() => {
        setPdfJsIncompatible(true)
      });
    }
	}, [canvasRef, pdfPromise]);

  useEffect(() => {
    if(pdf && !rendered){
      setRendered(true);
    }
  }, [pdf, rendered]);

  return <Box sx={sx}>
    <Stack
      direction="column"
      justifyContent="flex-start"
      alignItems="center"
      gap={3}
      sx={{ width: 1, height: 1, overflow: "auto", }}
      ref={containerRef}
    >
      {(new Array(pdf?.numPages || 0))
        .fill(undefined)
        .map((_, i) => <canvas
          ref={element => canvasRef.current[i] = element}
          style={{
            margin: 24,
            boxShadow: '0 0 8px rgba(0,0,0,0.1), 0 0 2px rgba(0,0,0,0.1)',
            background: '#fff'
          }}
        />)}
      {url && pdfJsIncompatible && <iframe
        src={`https://drive.google.com/viewerng/viewer?url=${encodeURIComponent(url)}&hl=it&pid=explorer&efh=false&a=v&chrome=false&embedded=true`}
        width="100%"
        height="100%"
        style={{
          border: 'none',
        }}
        title="PDF viewer"
      >
        <p>Non è possibile aprire PDF nel tuo browser.</p>
      </iframe>}
    </Stack>
  </Box>
}